import {
  chatDraftFilesAtomFamily,
  chatDraftTextAtomFamily,
  NEW_CHAT_DRAFT_KEY,
} from '@/atoms/chatAtoms';
import type { Editor } from '@tiptap/react';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';

const DEBOUNCE_MS = 300;

interface UseChatInputDraftOptions {
  /** The chat ID to use for draft storage. Pass null/undefined for new chats. */
  chatId: string | null | undefined;
  /** The TipTap editor instance to sync with */
  editor: Editor | null;
}

interface UseChatInputDraftReturn {
  /** The saved draft files */
  draftFiles: File[];
  /** Update the draft files */
  setDraftFiles: (files: File[]) => void;
  /** Clear both text and file drafts (call after sending a message) */
  clearDraft: () => void;
}

/**
 * Hook for persisting chat input drafts (text and files) across navigation.
 *
 * - Text drafts are stored in localStorage and persist across page refreshes
 * - File drafts are stored in memory and persist during navigation but not page refresh
 * - Text is automatically synced from the editor with debouncing
 * - Draft is cleared when clearDraft() is called (typically after sending)
 */
export const useChatInputDraft = ({
  chatId,
  editor,
}: UseChatInputDraftOptions): UseChatInputDraftReturn => {
  const draftKey = chatId ?? NEW_CHAT_DRAFT_KEY;

  const [draftText, setDraftText] = useAtom(chatDraftTextAtomFamily(draftKey));
  const [draftFiles, setDraftFiles] = useAtom(chatDraftFilesAtomFamily(draftKey));

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Store the draft key at the time of restoration to detect chatId changes
  const restoredForKeyRef = useRef<string | null>(null);

  // Restore draft text to editor on mount (only once per chatId)
  // We use requestAnimationFrame to defer restoration until after the editor
  // has fully initialized (avoids race condition with useBlockEditorMarkdown)
  useEffect(() => {
    if (!editor) return;

    // Only restore once per draftKey
    if (restoredForKeyRef.current === draftKey) return;

    // Only restore if there's saved content
    if (draftText) {
      // Defer restoration to next frame to ensure editor is fully ready
      // This avoids a race condition where useBlockEditorMarkdown's initialization
      // (with content: '') overwrites our restored content
      const frameId = requestAnimationFrame(() => {
        // Double-check editor is still empty (user might have started typing)
        if (!editor.isEmpty) {
          restoredForKeyRef.current = draftKey;
          return;
        }

        // Escape HTML entities to prevent XSS and ensure proper rendering
        const escapedText = draftText
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;')
          .replace(/\n/g, '<br>');
        // Use setContent to restore the draft - wrap in paragraph for TipTap.
        // emitUpdate: false — a restore isn't a live edit; letting 'update'
        // fire here let a restored "/…" draft reopen the shortcuts menu.
        editor.commands.setContent(`<p>${escapedText}</p>`, { emitUpdate: false });
        // Move cursor to end
        editor.commands.focus('end');
        // Mark as restored for this key AFTER successful restoration
        restoredForKeyRef.current = draftKey;
      });

      return () => cancelAnimationFrame(frameId);
    }
  }, [editor, draftText, draftKey]);

  // Listen to editor changes and save to draft (debounced)
  useEffect(() => {
    if (!editor) return;

    const handleUpdate = () => {
      // Clear existing debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Debounce the save to avoid excessive localStorage writes
      debounceTimerRef.current = setTimeout(() => {
        const text = (editor.getText() ?? '').trim();
        setDraftText(text);
      }, DEBOUNCE_MS);
    };

    editor.on('update', handleUpdate);

    return () => {
      editor.off('update', handleUpdate);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [editor, setDraftText]);

  // Clear both text and file drafts
  const clearDraft = useCallback(() => {
    setDraftText('');
    setDraftFiles([]);
  }, [setDraftText, setDraftFiles]);

  return {
    // Ensure draftFiles is always an array (for safety with mocks/edge cases)
    draftFiles: Array.isArray(draftFiles) ? draftFiles : [],
    setDraftFiles,
    clearDraft,
  };
};

/**
 * Hook to check if a chat has a saved draft.
 * Useful for showing draft indicators in chat lists (like Slack's pencil icon).
 *
 * @param chatId - The chat ID to check, or null/undefined for new chat
 * @returns true if the chat has a non-empty draft
 */
export const useChatHasDraft = (chatId: string | null | undefined): boolean => {
  const draftKey = chatId ?? NEW_CHAT_DRAFT_KEY;
  const draftText = useAtomValue(chatDraftTextAtomFamily(draftKey));
  return !!draftText && draftText.trim().length > 0;
};
